/**
 * Print Button Component.
 *
 * Opens a print-optimized window styled to match the application's
 * Apex Design System — brand gradient accents, warm slate tones,
 * professional typography, and polished card layouts.
 */

import { Printer } from "lucide-react";
import Button from "@/components/ui/Button";

interface PrintButtonProps {
  contentId?: string;
  getHtml?: () => string;
  label?: string;
  size?: "sm" | "md" | "lg";
}

/** Print stylesheet matching the Apex Design System v3. */
const PRINT_STYLES = `
  * { margin: 0; padding: 0; box-sizing: border-box; }

  body {
    font-family: 'DM Sans', 'Inter', system-ui, -apple-system, sans-serif;
    font-size: 11px;
    color: #1e1b2e;
    padding: 32px;
    line-height: 1.6;
    -webkit-font-smoothing: antialiased;
  }

  /* ── Document Header ── */
  .doc-header {
    border: 1px solid #e8e5f0;
    border-radius: 12px;
    overflow: hidden;
    margin-bottom: 24px;
  }
  .doc-header-accent {
    height: 4px;
    background: linear-gradient(90deg, #6d28d9, #8b5cf6, #f472b6);
  }
  .doc-header-body {
    padding: 20px 24px;
  }
  .doc-header-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 24px;
  }
  .doc-company-name {
    font-size: 16px;
    font-weight: 700;
    color: #0f0b1a;
  }
  .doc-company-detail {
    font-size: 10px;
    color: #64607a;
    margin-top: 2px;
  }
  .doc-type-label {
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.15em;
    color: #8b85a0;
    text-align: right;
  }
  .doc-number {
    font-size: 20px;
    font-weight: 700;
    font-family: 'JetBrains Mono', 'Fira Code', monospace;
    color: #6d28d9;
    text-align: right;
    margin-top: 2px;
  }
  .doc-meta {
    text-align: right;
    font-size: 10px;
    color: #64607a;
    margin-top: 8px;
  }
  .doc-meta p { margin: 1px 0; }
  .doc-status {
    display: inline-block;
    font-size: 9px;
    font-weight: 700;
    padding: 2px 10px;
    border-radius: 9999px;
    margin-top: 4px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  .status-default { background: #f1eff6; color: #64607a; }
  .status-success { background: #dcfce7; color: #166534; }
  .status-warning { background: #fef3c7; color: #92400e; }
  .status-danger  { background: #fee2e2; color: #991b1b; }
  .status-info    { background: #dbeafe; color: #1e40af; }

  /* ── Party Block ── */
  .doc-party {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #e8e5f0;
  }
  .doc-party-label {
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.15em;
    color: #8b85a0;
    margin-bottom: 4px;
  }
  .doc-party-name {
    font-size: 13px;
    font-weight: 600;
    color: #0f0b1a;
  }
  .doc-party-detail {
    font-size: 10px;
    color: #64607a;
    margin-top: 1px;
  }

  /* ── Section Cards ── */
  .section {
    border: 1px solid #e8e5f0;
    border-radius: 10px;
    margin-bottom: 16px;
    overflow: hidden;
  }
  .section-title {
    font-size: 11px;
    font-weight: 700;
    color: #0f0b1a;
    padding: 10px 16px;
    background: #f8f7fb;
    border-bottom: 1px solid #e8e5f0;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  .section-body {
    padding: 12px 16px;
  }

  /* ── Tables ── */
  table {
    width: 100%;
    border-collapse: collapse;
  }
  th {
    text-align: left;
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #8b85a0;
    padding: 8px 12px;
    border-bottom: 2px solid #e8e5f0;
    background: #faf9fd;
  }
  td {
    padding: 8px 12px;
    border-bottom: 1px solid #f1eff6;
    font-size: 11px;
    color: #1e1b2e;
  }
  tr:last-child td { border-bottom: none; }

  .text-right { text-align: right; }
  .text-center { text-align: center; }
  .font-mono { font-family: 'JetBrains Mono', monospace; font-size: 10px; }
  .font-bold { font-weight: 700; }
  .capitalize { text-transform: capitalize; }
  .text-muted { color: #8b85a0; }
  .text-brand { color: #6d28d9; }
  .text-success { color: #166534; }
  .text-danger { color: #991b1b; }

  /* A charge belonging to the line above it — indented, lighter, and without
     its own bottom rule so the pair reads as one item. */
  .charge-row td { border-bottom: none; font-size: 10px; padding-top: 2px; padding-bottom: 2px; }

  /* ── Totals ── */
  .totals-table { max-width: 300px; margin-left: auto; }
  .totals-table td { padding: 4px 12px; border-bottom: none; }
  .totals-table .total-row td {
    border-top: 2px solid #e8e5f0;
    padding-top: 8px;
    font-weight: 700;
    font-size: 13px;
  }

  /* ── Summary Grid ── */
  .summary-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    margin-bottom: 16px;
  }
  .summary-card {
    border: 1px solid #e8e5f0;
    border-radius: 8px;
    padding: 12px;
  }
  .summary-label { font-size: 9px; color: #8b85a0; text-transform: uppercase; letter-spacing: 0.05em; }
  .summary-value { font-size: 16px; font-weight: 700; color: #0f0b1a; margin-top: 4px; }

  /* ── Notes ── */
  .notes { padding: 12px 16px; font-size: 11px; color: #64607a; font-style: italic; }

  /* ── Footer ── */
  .print-footer {
    margin-top: 32px;
    padding-top: 12px;
    border-top: 1px solid #e8e5f0;
    font-size: 9px;
    color: #8b85a0;
    text-align: center;
  }

  @media print {
    body { padding: 16px; }
    .no-print { display: none; }
    .doc-header, .section { break-inside: avoid; }
  }
`;

function PrintButton({
  contentId,
  getHtml,
  label = "Print",
  size = "sm",
}: PrintButtonProps) {
  const handlePrint = () => {
    let html: string;

    if (getHtml) {
      html = getHtml();
    } else if (contentId) {
      const el = document.getElementById(contentId);
      if (!el) return;
      html = el.innerHTML;
    } else {
      return;
    }

    const printWindow = window.open("", "_blank", "width=850,height=700");
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Print — TradeRoute</title>
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400&display=swap" rel="stylesheet" />
        <style>${PRINT_STYLES}</style>
      </head>
      <body>
        ${html}
        <div class="print-footer">
          Generated by TradeRoute &middot; ${new Date().toLocaleString()}
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 300);
  };

  return (
    <Button variant="secondary" size={size} onClick={handlePrint}>
      <Printer className="h-4 w-4" /> {label}
    </Button>
  );
}

export default PrintButton;
